<template>
    <el-dialog
        :visible="visible"
        :title="isHrDepartment ? 'Thông tin Thử việc & Offer (Chỉ xem)' : 'Điền thông tin Thử việc & Offer (Teamlead)'"
        width="580px"
        custom-class="rcv-dialog rcv-offer-procedure-dialog"
        :close-on-click-modal="false"
        append-to-body
        @update:visible="handleClose"
    >
        <div v-if="candidate" class="rcv-offer-proc">
            <!-- Cảnh báo nếu tài khoản là HR (chỉ xem, không được thao tác) -->
            <el-alert
                v-if="isHrDepartment"
                type="warning"
                title="Chỉ Teamlead mới có quyền điền thông tin Thử việc & Offer"
                description="Tài khoản thuộc bộ phận Nhân sự (HR) chỉ có quyền xem thông tin, không được chỉnh sửa hoặc lưu thông tin bước này."
                show-icon
                :closable="false"
                class="mb-3"
            />

            <!-- Thẻ tóm tắt thông tin ứng viên -->
            <div class="rcv-offer-proc__candidate-card">
                <div class="rcv-offer-proc__candidate-header">
                    <div class="rcv-offer-proc__avatar">
                        <i class="el-icon-user-solid"></i>
                    </div>
                    <div class="rcv-offer-proc__info">
                        <div class="rcv-offer-proc__name">
                            {{ candidate.name || ("Ứng viên #" + candidate.id) }}
                            <el-tag size="mini" type="success" effect="dark" class="ml-2">
                                <i class="el-icon-check"></i> Đạt PV V2
                            </el-tag>
                        </div>
                        <div class="rcv-offer-proc__meta">
                            <span v-if="candidateJobTitle" class="rcv-offer-proc__meta-item">
                                <i class="el-icon-suitcase"></i> {{ candidateJobTitle }}
                            </span>
                            <span v-if="candidate.phone" class="rcv-offer-proc__meta-item">
                                <i class="el-icon-phone-outline"></i> {{ candidate.phone }}
                            </span>
                            <span v-if="candidate.email" class="rcv-offer-proc__meta-item">
                                <i class="el-icon-message"></i> {{ candidate.email }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Form điền thông tin Offer & Thử việc -->
            <el-form
                ref="formRef"
                :model="form"
                :rules="rules"
                :disabled="isHrDepartment"
                label-position="top"
                size="small"
                @submit.native.prevent="handleSubmit"
            >
                <!-- Mức lương (salary) -->
                <el-form-item label="Mức lương (VNĐ)" prop="salary">
                    <el-input
                        ref="salaryInput"
                        v-model="displaySalary"
                        placeholder="VD: 15.000.000"
                        class="w-100"
                        clearable
                        @input="handleSalaryInput"
                        @clear="handleSalaryClear"
                    >
                        <template slot="append">VNĐ</template>
                    </el-input>
                    <div v-if="form.salary" class="rcv-form__hint font-bold text-success mt-1">
                        <i class="el-icon-money"></i> {{ formatMoney(form.salary) }} VNĐ
                    </div>
                </el-form-item>

                <!-- Thời gian thử việc: Ngày thử việc & Ngày kết thúc thử việc -->
                <div class="rcv-form-row">
                    <el-form-item label="Ngày thử việc (Bắt đầu)" prop="probation_start_date" class="rcv-form-col">
                        <el-date-picker
                            v-model="form.probation_start_date"
                            type="date"
                            value-format="yyyy-MM-dd"
                            format="dd/MM/yyyy"
                            placeholder="Chọn ngày thử việc"
                            class="w-100"
                            @change="onStartDateChange"
                        />
                    </el-form-item>

                    <el-form-item label="Ngày kết thúc thử việc" prop="probation_end_date" class="rcv-form-col">
                        <el-date-picker
                            v-model="form.probation_end_date"
                            type="date"
                            value-format="yyyy-MM-dd"
                            format="dd/MM/yyyy"
                            placeholder="Chọn ngày kết thúc"
                            class="w-100"
                        />
                    </el-form-item>
                </div>

                <!-- Phím tắt tính thời gian thử việc nhanh -->
                <div v-if="!isHrDepartment && form.probation_start_date" class="rcv-quick-duration mb-3">
                    <span class="text-muted" style="font-size: 12px; margin-right: 6px;">Chọn nhanh:</span>
                    <el-button size="mini" type="text" @click="setProbationDuration(1)">
                        + 1 tháng thử việc
                    </el-button>
                    <span class="text-muted">·</span>
                    <el-button size="mini" type="text" @click="setProbationDuration(2)">
                        + 2 tháng thử việc
                    </el-button>
                    <span class="text-muted">·</span>
                    <el-button size="mini" type="text" @click="setProbationDuration(3)">
                        + 3 tháng thử việc
                    </el-button>
                </div>

                <!-- Tiến trình tiếp theo: Cố định Cần làm thủ tục Pass (14) -->
                <el-form-item label="Tiến trình cập nhật sau khi lưu">
                    <div class="rcv-next-step-badge">
                        <div class="rcv-next-step-badge__icon">
                            <i class="el-icon-circle-check text-success"></i>
                        </div>
                        <div class="rcv-next-step-badge__content">
                            <div class="rcv-next-step-badge__title">
                                Cần làm thủ tục Pass 
                            </div>
                            <div class="rcv-next-step-badge__desc">
                                Hồ sơ sẽ tự động chuyển sang trạng thái <b>Cần làm thủ tục Pass</b> để bộ phận Nhân sự tiến hành thủ tục tiếp nhận nhân sự.
                            </div>
                        </div>
                    </div>
                </el-form-item>

                <!-- Ghi chú chi tiết về Offer / Thử việc -->
                <el-form-item label="Ghi chú chi tiết về Offer & Thử việc (Tuỳ chọn)">
                    <el-input
                        v-model="form.note"
                        type="textarea"
                        :rows="3"
                        placeholder="Nhập ghi chú chi tiết về mức lương chính thức sau thử việc, phụ cấp, thoả thuận..."
                        maxlength="1000"
                        show-word-limit
                    />
                </el-form-item>
            </el-form>
        </div>

        <span slot="footer" class="dialog-footer">
            <el-button size="small" :disabled="submitting" @click="handleClose">Đóng</el-button>
            <el-button
                v-if="!isHrDepartment"
                type="success"
                size="small"
                icon="el-icon-check"
                :loading="submitting"
                @click="handleSubmit"
            >
                Xác nhận &amp; Cần làm thủ tục Pass
            </el-button>
        </span>
    </el-dialog>
</template>

<script>
import moment from "moment-timezone";
import { CAN_LAM_THU_TUC_PASS } from "@/core/config/recruitmentCvs/recruitmentCvsOption";

export default {
    name: "OfferProcedureDialog",
    props: {
        visible: { type: Boolean, default: false },
        candidate: { type: Object, default: null },
        orders: { type: Array, default: () => [] },
        submitting: { type: Boolean, default: false },
        isHrDepartment: { type: Boolean, default: false },
    },
    data() {
        return {
            displaySalary: "",
            form: {
                salary: null,
                probation_start_date: "",
                probation_end_date: "",
                note: "",
            },
            rules: {
                salary: [
                    {
                        required: true,
                        message: "Vui lòng nhập Mức lương (VNĐ)",
                        trigger: ["blur", "change"],
                    },
                    {
                        validator: (rule, value, callback) => {
                            if (value === null || value === undefined || value === "") {
                                callback(new Error("Vui lòng nhập Mức lương (VNĐ)"));
                                return;
                            }
                            if (Number(value) <= 0) {
                                callback(new Error("Mức lương phải lớn hơn 0"));
                                return;
                            }
                            callback();
                        },
                        trigger: ["blur", "change"],
                    },
                ],
                probation_start_date: [
                    {
                        required: true,
                        message: "Vui lòng chọn Ngày thử việc (Bắt đầu)",
                        trigger: ["blur", "change"],
                    },
                    {
                        validator: (rule, value, callback) => {
                            if (!value) {
                                callback(new Error("Vui lòng chọn Ngày thử việc (Bắt đầu)"));
                                return;
                            }
                            callback();
                        },
                        trigger: ["blur", "change"],
                    },
                ],
                probation_end_date: [
                    {
                        required: true,
                        message: "Vui lòng chọn Ngày kết thúc thử việc",
                        trigger: ["blur", "change"],
                    },
                    {
                        validator: (rule, value, callback) => {
                            if (!value) {
                                callback(new Error("Vui lòng chọn Ngày kết thúc thử việc"));
                                return;
                            }
                            if (this.form.probation_start_date && value < this.form.probation_start_date) {
                                callback(new Error("Ngày kết thúc thử việc phải sau Ngày thử việc (Bắt đầu)"));
                                return;
                            }
                            callback();
                        },
                        trigger: ["blur", "change"],
                    },
                ],
            },
        };
    },
    computed: {
        candidateJobTitle() {
            if (!this.candidate) return "";
            const c = this.candidate;
            const found = this.orders.find((o) =>
                String(o.id) === String(c.job_id) ||
                String(o.id) === String(c.order_id) ||
                String(o.id) === String(c.raw_job_id) ||
                (o.recruitment_job_id && String(o.recruitment_job_id) === String(c.job_id)) ||
                (o.recruitment_job_id && String(o.recruitment_job_id) === String(c.raw_job_id))
            );
            return found ? (found.title || found.position_title || "") : (c.order_title || "");
        },
    },
    watch: {
        visible(val) {
            if (val) {
                this.initForm();
            }
        },
        candidate() {
            if (this.visible) {
                this.initForm();
            }
        },
    },
    methods: {
        formatMoney(num) {
            if (num === null || num === undefined || num === "") return "0";
            return Number(num).toLocaleString("vi-VN");
        },
        initForm() {
            const c = this.candidate || {};
            const existingSalary = c.salary || c.expected_salary || null;
            const existingStart = c.probation_start_date || c.start_date || c.probation_date || "";
            const existingEnd = c.probation_end_date || "";
            const salaryNum = existingSalary ? Number(existingSalary) : null;

            this.form = {
                salary: salaryNum,
                probation_start_date: existingStart ? moment(existingStart).format("YYYY-MM-DD") : "",
                probation_end_date: existingEnd ? moment(existingEnd).format("YYYY-MM-DD") : "",
                note: c.offer_note || "",
            };
            this.displaySalary = salaryNum ? salaryNum.toLocaleString("vi-VN") : "";

            // Nếu đã có ngày bắt đầu mà chưa có ngày kết thúc: gợi ý mặc định 2 tháng
            if (this.form.probation_start_date && !this.form.probation_end_date) {
                this.setProbationDuration(2);
            }

            this.$nextTick(() => {
                if (this.$refs.formRef) {
                    this.$refs.formRef.clearValidate();
                }
            });
        },
        handleSalaryClear() {
            this.displaySalary = "";
            this.form.salary = null;
            if (this.$refs.formRef) {
                this.$refs.formRef.validateField("salary");
            }
        },
        handleSalaryInput(val) {
            if (!val) {
                this.displaySalary = "";
                this.form.salary = null;
                if (this.$refs.formRef) {
                    this.$refs.formRef.validateField("salary");
                }
                return;
            }

            const inputEl = this.$refs.salaryInput && this.$refs.salaryInput.$refs.input;
            const oldVal = String(val);
            const oldCursorPos = inputEl ? inputEl.selectionStart : null;
            const oldDigitsBeforeCursor = oldCursorPos !== null
                ? oldVal.slice(0, oldCursorPos).replace(/\D/g, "").length
                : 0;

            const digits = oldVal.replace(/\D/g, "");
            if (!digits) {
                this.displaySalary = "";
                this.form.salary = null;
                if (this.$refs.formRef) {
                    this.$refs.formRef.validateField("salary");
                }
                return;
            }

            const num = Number(digits);
            this.form.salary = num;
            const formatted = num.toLocaleString("vi-VN");
            this.displaySalary = formatted;

            this.$nextTick(() => {
                if (inputEl) {
                    inputEl.value = formatted;
                    if (oldCursorPos !== null) {
                        let newCursor = 0;
                        let digitCount = 0;
                        for (let i = 0; i < formatted.length; i++) {
                            if (/\d/.test(formatted[i])) {
                                digitCount++;
                            }
                            if (digitCount === oldDigitsBeforeCursor) {
                                newCursor = i + 1;
                                break;
                            }
                        }
                        if (newCursor === 0 && oldDigitsBeforeCursor === 0) {
                            newCursor = 0;
                        } else if (digitCount < oldDigitsBeforeCursor) {
                            newCursor = formatted.length;
                        }
                        inputEl.setSelectionRange(newCursor, newCursor);
                    }
                }
                if (this.$refs.formRef) {
                    this.$refs.formRef.validateField("salary");
                }
            });
        },
        onStartDateChange(val) {
            if (!val) return;
            // Tự động tính ngày kết thúc 2 tháng nếu chưa có
            if (!this.form.probation_end_date) {
                this.setProbationDuration(2);
            }
        },
        setProbationDuration(months = 2) {
            if (!this.form.probation_start_date) return;
            const d = moment(this.form.probation_start_date).add(months, "months").subtract(1, "days");
            this.form.probation_end_date = d.format("YYYY-MM-DD");
            this.$nextTick(() => {
                if (this.$refs.formRef) {
                    this.$refs.formRef.validateField("probation_end_date");
                }
            });
        },
        handleClose() {
            this.$emit("update:visible", false);
        },
        handleSubmit() {
            if (this.isHrDepartment) {
                this.$message.warning("Chỉ Teamlead mới có quyền điền và lưu thông tin Thử việc & Offer");
                return;
            }
            if (!this.candidate) return;
            this.$refs.formRef.validate((valid) => {
                if (!valid) return;

                const salary = this.form.salary;
                const startDate = this.form.probation_start_date;
                const endDate = this.form.probation_end_date;
                const note = (this.form.note || "").trim();

                let noteContent = `[Điền thông tin Thử việc & Offer - Chuyển Cần làm thủ tục Pass (14)]`;
                if (salary) {
                    noteContent += `\nMức lương: ${this.formatMoney(salary)} VNĐ`;
                }
                if (startDate) {
                    noteContent += `\nNgày thử việc: ${moment(startDate).format("DD/MM/YYYY")}`;
                }
                if (endDate) {
                    noteContent += `\nNgày kết thúc thử việc: ${moment(endDate).format("DD/MM/YYYY")}`;
                }
                if (note) {
                    noteContent += `\nGhi chú: ${note}`;
                }

                const payload = {
                    id: this.candidate.id,
                    status: CAN_LAM_THU_TUC_PASS, // 14
                    salary: salary,
                    expected_salary: salary,
                    probation_start_date: startDate,
                    probation_end_date: endDate,
                    start_date: startDate,
                    probation_date: startDate,
                    note: noteContent,
                    message: noteContent,
                    offer_note: note,
                    candidate: {
                        ...this.candidate,
                        status: CAN_LAM_THU_TUC_PASS,
                        salary: salary,
                        probation_start_date: startDate,
                        probation_end_date: endDate,
                        start_date: startDate,
                        probation_date: startDate,
                        offer_note: note,
                    },
                };

                this.$emit("submit", payload);
                this.handleClose();
            });
        },
    },
};
</script>

<style scoped>
.rcv-offer-proc {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.rcv-offer-proc__candidate-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px 14px;
}
.rcv-offer-proc__candidate-header {
    display: flex;
    align-items: center;
    gap: 12px;
}
.rcv-offer-proc__avatar {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: linear-gradient(135deg, #10b981, #059669);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
}
.rcv-offer-proc__info {
    flex: 1;
    min-width: 0;
}
.rcv-offer-proc__name {
    font-size: 15px;
    font-weight: 700;
    color: #1e293b;
    display: flex;
    align-items: center;
}
.rcv-offer-proc__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 4px;
    font-size: 12.5px;
    color: #64748b;
}
.rcv-offer-proc__meta-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.rcv-offer-proc__notice {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #166534;
    font-size: 12.5px;
    line-height: 1.5;
    padding: 8px 10px;
    border-radius: 6px;
    margin-top: 10px;
}
.rcv-offer-proc__notice i {
    font-size: 15px;
    color: #10b981;
    margin-top: 2px;
    flex-shrink: 0;
}
.rcv-form-row {
    display: flex;
    gap: 12px;
}
.rcv-form-col {
    flex: 1;
}
.rcv-form__hint {
    font-size: 12px;
    margin-top: 4px;
}
.rcv-quick-duration {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    background: #f0fdfa;
    border: 1px dashed #5eead4;
    border-radius: 6px;
}
.rcv-quick-duration /deep/ .el-button--text {
    padding: 0;
    font-weight: 600;
    color: #0d9488;
}
.rcv-next-step-badge {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    background: #f0fdf4;
    border: 1px solid #86efac;
    border-radius: 8px;
    padding: 10px 14px;
}
.rcv-next-step-badge__icon {
    font-size: 24px;
    color: #10b981;
    margin-top: 2px;
}
.rcv-next-step-badge__title {
    font-size: 14px;
    font-weight: 700;
    color: #166534;
}
.rcv-next-step-badge__title span {
    font-size: 12px;
    font-weight: normal;
    color: #059669;
}
.rcv-next-step-badge__desc {
    font-size: 12.5px;
    color: #15803d;
    margin-top: 2px;
    line-height: 1.4;
}
</style>
